import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApplicationProductTypeEnum,
  InlineAlertType,
  Mutation,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  router,
  Stack,
  useFocusEffect,
  useRouter,
} from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import {
  UpdateApplicationData,
  UpdateApplicationHandler,
} from 'features/application/hooks/useUpdateApplication';
import { PersonalInfoSchema } from 'features/application/screens/PersonalInfo/PersonalInfo.schema';
import React, { useCallback, useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  BackHandler,
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import StartNewApplication from 'services/graphql/mutations/StartNewApplication';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS } from 'shared/constants/platform';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';
import { AnyVariables, UseMutationState } from 'urql';

interface PersonalInfoProps {
  isUpdateApplicationSuccess?: boolean;
  firstName: string;
  lastName: string;
  hasApplicationLoaded: boolean;
  isReapplication: boolean;
  updateApplication: UpdateApplicationHandler;
  updateApplicationResult: UseMutationState<UpdateApplicationData>;
  isUpdateApplicationLoading: boolean;
  reExecuteQuery: () => void;
  fetching: boolean;
  error?: Error | null;
  buttonLabel: string;
  isReactivation?: boolean;
  nextRoute: Parameters<typeof router.push>[0];
  backRoute?: Parameters<typeof router.replace>[0];
  applicationId: string;
}

const PersonalInfo = ({
  updateApplication,
  updateApplicationResult,
  isUpdateApplicationLoading,
  reExecuteQuery,
  fetching,
  error,
  isUpdateApplicationSuccess,
  firstName,
  lastName,
  buttonLabel,
  isReactivation,
  nextRoute,
  backRoute,
  applicationId,
  hasApplicationLoaded,
  isReapplication,
}: PersonalInfoProps) => {
  const router = useRouter();
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'personalInfo',
  });

  const { bottom } = useSafeAreaInsets();

  useStepStartedFocusAnalytics(
    hasApplicationLoaded
      ? {
          analyticsId: 'stepStarted',
          customAttributes: {
            step_name: 'personal_info_name',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  const {
    control,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
    },
    resolver: zodResolver(PersonalInfoSchema),
  });

  useEffect(() => {
    if (firstName && lastName) {
      reset({
        firstName: firstName as string,
        lastName: lastName as string,
      });
    }
  }, [firstName, lastName, reset]);

  useStepStartedFocusAnalytics(
    isUpdateApplicationSuccess
      ? {
          analyticsId: 'personalInformationCompleted',
          customAttributes: {
            step_name: 'personal_info_name',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  const updateApplicationData = async (applicationId?: string) => {
    const { firstName, lastName } = getValues();
    const updates = {
      firstName: firstName,
      lastName: lastName,
    } as AnyVariables;

    if (applicationId && updates) {
      updates.applicationId = applicationId;
    }

    await updateApplication({
      updates,
      nextRoute: nextRoute,
    });
  };

  const [
    startNewApplicationMutationResult,
    startNewApplicationExecuteMutation,
  ] = useCancellableMutation<
    Mutation,
    { idempotencyId: string; products: ApplicationProductTypeEnum[] }
  >(StartNewApplication);

  const startNewApplicationApi = async () => {
    const { data: startNewApplicationMutationData } =
      await startNewApplicationExecuteMutation({
        idempotencyId: getUuid(),
        products: [ApplicationProductTypeEnum.Checking],
      });
    if (
      startNewApplicationMutationData?.startNewApplication?.__typename ===
      Typenames.StartNewApplicationSuccess
    ) {
      await updateApplicationData(
        startNewApplicationMutationData.startNewApplication.application.id,
      );
    }
  };

  const onButtonAction = async () => {
    if (!applicationId || isReapplication) {
      await startNewApplicationApi();
      return;
    }
    await updateApplicationData();
  };
  const { handleLogout } = useContext<AuthContextProps>(AuthContext);

  const handleBackButtonAction = useCallback(() => {
    if (backRoute) {
      router.replace(backRoute);
    } else {
      void handleLogout();
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLogout, router]);

  const { height } = useWindowDimensions();

  //useBackHandler hook causing issue here not getting removed when navigating to next screen
  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackButtonAction,
      );

      return () => backHandler.remove();
    }, [handleBackButtonAction]),
  );

  const isKeyboardVisible = useKeyboardVisibility();

  const onSubmitEditingAction = async () => {
    if (isValid) {
      await onButtonAction();
    }
  };

  return (
    <View style={styles.container} testID="inline-loader">
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={
          <View style={styles.loaderContainer}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              illustrationKey="PLACEHOLDER_A"
              tryAgainAction={reExecuteQuery}
              screenAnalyticsEventKey="signupFlowError"
            />
          ) : null
        }
      />
      <KeyboardAvoidingView
        style={styles.keyboardContainer}
        behavior={isIOS ? 'padding' : 'height'}
        keyboardVerticalOffset={getKeyboardVerticalOffset(
          height,
          isKeyboardVisible,
        )}
      >
        <ScrollView contentContainerStyle={styles.keyboardContainer}>
          <Stack.Screen
            options={{
              headerShown: true,
              headerBackVisible: true,
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar
                  {...props}
                  primaryOnPress={handleBackButtonAction}
                />
              ),
            }}
          />
          {updateApplicationResult.error ? (
            <View style={styles.inlineAlertContainer}>
              <InlineAlert
                alertType={InlineAlertType.Error}
                bodyText={translatedText('inlineAlert.errorTitle')}
                iconKeyType="ERROR"
                onDismiss={() => {}}
              />
            </View>
          ) : null}
          <View style={styles.formContainer}>
            <View style={styles.personalInfoFormContainer}>
              <TitleLgText numberOfLines={0} style={styles.heading}>
                {translatedText('title')}
              </TitleLgText>
              <Controller
                control={control}
                name="firstName"
                render={({ field: { onChange, onBlur, value } }) => (
                  <View style={styles.input}>
                    <VXLTextInput
                      testID="text-input-textField.firstName"
                      analyticsId="firstNameFieldClicked"
                      errorText={errors.firstName?.message ?? ''}
                      labelText={translatedText('textField.firstName')}
                      onChangeText={(text) => {
                        onChange(text);
                        onBlur();
                      }}
                      value={value}
                      autoFocus
                      autoCapitalize="none"
                      onSubmitEditing={onSubmitEditingAction}
                    />
                  </View>
                )}
              />
              <Controller
                control={control}
                name="lastName"
                render={({ field: { onChange, onBlur, value } }) => (
                  <View style={styles.lastNameInput}>
                    <VXLTextInput
                      testID="text-input-textField.lastName"
                      analyticsId="lastNameFieldClicked"
                      labelText={translatedText('textField.lastName')}
                      onChangeText={(text) => {
                        onChange(text);
                        onBlur();
                      }}
                      value={value}
                      autoCapitalize="none"
                      errorText={errors.lastName?.message ?? ''}
                      onSubmitEditing={onSubmitEditingAction}
                    />
                  </View>
                )}
              />
            </View>
          </View>
        </ScrollView>
        <View
          style={[
            styles.buttonContainer,
            {
              paddingBottom: isKeyboardVisible
                ? theme.spacing.xl
                : bottom + theme.spacing.m,
            },
          ]}
        >
          <Button
            label={buttonLabel}
            isDisabled={!isValid}
            isLoading={
              startNewApplicationMutationResult.fetching ||
              isUpdateApplicationLoading
            }
            onPress={onButtonAction}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    marginHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: '100%',
  },
  formContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
  heading: {
    marginBottom: theme.spacing.m,
  },
  inlineAlertContainer: {
    padding: theme.spacing.m,
  },
  input: {
    width: '100%',
  },
  keyboardContainer: {
    flex: 1,
  },
  lastNameInput: {
    marginTop: theme.spacing.m,
    width: '100%',
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  personalInfoFormContainer: {
    flex: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default PersonalInfo;
